/**
 * screenJsonPortability.js
 * -------------------------------------------------------------------------
 * Helpers for the Download / Upload-JSON feature in the Edit-Screens popup
 * (SubprogramDetailAdmin.jsx).
 *
 * Each screen type stores a JSON object in the `content` (jsonb) column of the
 * `stagingscreencontent` Postgres table. The GET endpoint
 * (`/api/staging/:type/:projectId/:screenId`) returns that object with a
 * runtime-injected `meta` block (see getXxxMeta in backend/server.js).
 *
 * This module centralises three concerns so a downloaded file can be moved
 * between boards/projects safely:
 *
 *   exportScreenJson(type, content)   -> object to write to the .json download
 *   validateScreenJson(type, content) -> { valid, error } for an uploaded file
 *   prepareImportJson(type, content, ctx) -> object to PUT back to staging
 *
 * Portability model (mirrors the project spec):
 *   - meta.audioBaseUrl is NEVER portable (points at the source project's audio
 *     folder). It is always stripped on export AND import. The backend re-injects
 *     the correct value for the target project on the next GET.
 *   - meta.modelPath is allowed and kept.
 *   - PlayDynamex / Simulate / Integrate are identical across boards -> fully
 *     portable, nothing board-specific to strip beyond audioBaseUrl.
 *   - Assembly / Coding(hex) / Play embed board-specific identifiers
 *     (ports, components, accessory ids, byte arrays, cross-screen refs). Those
 *     are stripped on export and RESTORED as neutral placeholders on import,
 *     using the TARGET project/board as the source of truth — never copied
 *     blindly from the uploaded file.
 */

// ── Screen types that share one identical, fully-portable structure ──
const PORTABLE_TYPES = ['playdynamex', 'simulate', 'integrate']

// Board background image filename rendered by the live Play screen, keyed by
// device. Mirrors BOARD_CONFIG.image in Play/PlayAdmin.jsx (toki.png / poki.png).
const resolveBoardImage = (device) => {
    const key = (device || 'Toki').toString().trim().toLowerCase()
    if (key.startsWith('poki')) return 'poki.png'
    return 'toki.png'
}

const isPlainObject = (v) =>
    v !== null && typeof v === 'object' && !Array.isArray(v)

const deepClone = (v) => JSON.parse(JSON.stringify(v))

/** Remove the non-portable meta.audioBaseUrl in place (keeps meta.modelPath). */
const stripAudioBaseUrl = (content) => {
    if (isPlainObject(content) && isPlainObject(content.meta)) {
        const { audioBaseUrl, ...restMeta } = content.meta
        content.meta = restMeta
    }
    return content
}

/* ───────────────────────────── Validation ───────────────────────────── */

// Tiny path-aware validator. Each `check*` throws a descriptive Error that
// validateScreenJson turns into a { valid:false, error } result.
const fail = (path, msg) =>
    new Error(`"${path}" ${msg}`)

const expectObject = (v, path) => {
    if (!isPlainObject(v)) throw fail(path, 'must be a JSON object')
}
const expectArray = (v, path) => {
    if (!Array.isArray(v)) throw fail(path, 'must be an array')
}
const expectString = (v, path) => {
    if (typeof v !== 'string') throw fail(path, 'must be a string')
}
const expectNumber = (v, path) => {
    if (typeof v !== 'number' || Number.isNaN(v))
        throw fail(path, 'must be a number')
}

/** Shared validator for the three portable step-based screens. */
const validatePortable = (content, label) => {
    expectObject(content, label)
    expectString(content.title, `${label}.title`)
    expectNumber(content.totalSteps, `${label}.totalSteps`)
    expectArray(content.steps, `${label}.steps`)
    content.steps.forEach((step, i) => {
        const p = `${label}.steps[${i}]`
        expectObject(step, p)
        expectNumber(step.stepNumber, `${p}.stepNumber`)
        expectString(step.title, `${p}.title`)
        expectString(step.instruction, `${p}.instruction`)
        // animationName is optional (e.g. plain Playdynamex steps omit it) but
        // must be a string when present.
        if (step.animationName !== undefined)
            expectString(step.animationName, `${p}.animationName`)
    })
}

const validateAssembly = (content) => {
    expectObject(content, 'assembly')
    expectString(content.title, 'assembly.title')
    expectNumber(content.totalSteps, 'assembly.totalSteps')
    expectArray(content.steps, 'assembly.steps')
    content.steps.forEach((step, i) => {
        const p = `assembly.steps[${i}]`
        expectObject(step, p)
        expectNumber(step.stepNumber, `${p}.stepNumber`)
        expectString(step.title, `${p}.title`)
        expectString(step.instruction, `${p}.instruction`)
    })
    if (content.finalStepTitle !== undefined)
        expectString(content.finalStepTitle, 'assembly.finalStepTitle')
    if (content.finalStepInstruction !== undefined)
        expectString(
            content.finalStepInstruction,
            'assembly.finalStepInstruction'
        )
}

const validateHex = (content) => {
    expectObject(content, 'hex')
    expectString(content.version, 'hex.version')
    expectObject(content.meta, 'hex.meta')
    expectString(content.meta.title, 'hex.meta.title')
    expectArray(content.codeSteps, 'hex.codeSteps')
    content.codeSteps.forEach((cs, i) => {
        const p = `hex.codeSteps[${i}]`
        expectObject(cs, p)
        expectNumber(cs.stepId, `${p}.stepId`)
        expectString(cs.type, `${p}.type`)
        expectString(cs.action, `${p}.action`)
        expectString(cs.message, `${p}.message`)
        expectArray(cs.subSteps, `${p}.subSteps`)
        cs.subSteps.forEach((ss, j) => {
            const sp = `${p}.subSteps[${j}]`
            expectObject(ss, sp)
            expectString(ss.action, `${sp}.action`)
            expectString(ss.message, `${sp}.message`)
        })
    })
}

const validatePlay = (content) => {
    expectObject(content, 'play')
    expectString(content.title, 'play.title')
    expectString(content.layoutMode, 'play.layoutMode')
    expectNumber(content.totalSteps, 'play.totalSteps')
    expectArray(content.components, 'play.components')
    content.components.forEach((c, i) => {
        const p = `play.components[${i}]`
        expectObject(c, p)
        expectString(c.id, `${p}.id`)
        expectString(c.type, `${p}.type`)
    })
    expectArray(content.steps, 'play.steps')
    content.steps.forEach((s, i) => expectObject(s, `play.steps[${i}]`))
}

/* ─────────────────────────────── Export ─────────────────────────────── */

const exportAssembly = (content) => {
    const out = stripAudioBaseUrl(deepClone(content))
    // Board-specific accessory selections + per-step wiring are not portable.
    delete out.externalAccessories
    delete out.internalAccessories
    if (Array.isArray(out.steps)) {
        out.steps = out.steps.map((step) => {
            const { port, component, ...rest } = step
            return rest
        })
    }
    return out
}

const exportHex = (content) => {
    const out = stripAudioBaseUrl(deepClone(content))
    // assemblyScreenId references a specific Assembly screen in the source
    // project — not portable. codeSteps are a recorded, board-coupled tutorial
    // and are kept intact (cross-board coding is re-recorded against the target
    // board, per spec).
    delete out.assemblyScreenId
    return out
}

const exportPlay = (content) => {
    const out = stripAudioBaseUrl(deepClone(content))
    // Derived / board-specific top-level fields (regenerated by PlayAdmin on save).
    delete out.settingbytes
    delete out.assemblyScreenId
    delete out.projectId
    if (Array.isArray(out.components)) {
        out.components = out.components.map((c) => {
            const comp = { ...c }
            delete comp.port // board port wiring
            if (isPlainObject(comp.properties)) {
                const props = { ...comp.properties }
                delete props.bytes // generated byte arrays
                delete props.settingbytes
                // Board background image is resolved from the target board on import.
                if (comp.type === 'board' || comp.type === 'toki_board')
                    delete props.image
                comp.properties = props
            }
            return comp
        })
    }
    return out
}

/* ─────────────────────────────── Import ─────────────────────────────── */

const importPortable = (content) => stripAudioBaseUrl(deepClone(content))

const importAssembly = (content) => {
    const out = stripAudioBaseUrl(deepClone(content))
    // Restore board-specific fields as empty placeholders — the admin re-selects
    // accessories/ports in the editor against the target board.
    out.internalAccessories = []
    out.externalAccessories = []
    if (Array.isArray(out.steps)) {
        out.steps = out.steps.map((step) => ({
            ...step,
            component: '',
            port: '',
        }))
    }
    return out
}

const importHex = (content) => {
    const out = stripAudioBaseUrl(deepClone(content))
    // Restore the cross-screen reference as a placeholder; admin re-links the
    // Assembly screen for the target project in the editor.
    out.assemblyScreenId = ''
    return out
}

const importPlay = (content, ctx = {}) => {
    const out = stripAudioBaseUrl(deepClone(content))
    const { projectId, device } = ctx
    // Use the TARGET project/board as the source of truth.
    out.projectId = projectId || out.projectId || ''
    out.assemblyScreenId = ''
    out.settingbytes = null // regenerated by PlayAdmin on next save
    const boardImage = resolveBoardImage(device)
    if (Array.isArray(out.components)) {
        out.components = out.components.map((c) => {
            const comp = { ...c }
            comp.port = null // board wiring re-selected in the editor
            const props = isPlainObject(comp.properties)
                ? { ...comp.properties }
                : {}
            delete props.bytes
            delete props.settingbytes
            if (comp.type === 'board' || comp.type === 'toki_board') {
                comp.type = 'board' // normalise legacy type
                props.image = boardImage // target board background
            }
            comp.properties = props
            return comp
        })
    }
    return out
}

/* ───────────────────────────── Public API ───────────────────────────── */

/**
 * Build the object to write into the downloaded .json file for a screen.
 * Strips every non-portable / board-specific field per screen type.
 */
export const exportScreenJson = (type, content) => {
    if (!isPlainObject(content)) return content
    switch (type) {
        case 'assembly':
            return exportAssembly(content)
        case 'hex':
            return exportHex(content)
        case 'play':
            return exportPlay(content)
        case 'playdynamex':
        case 'simulate':
        case 'integrate':
            return stripAudioBaseUrl(deepClone(content))
        default:
            return stripAudioBaseUrl(deepClone(content))
    }
}

/**
 * Validate an uploaded JSON object against the expected schema for `type`.
 * Returns { valid: true } or { valid: false, error: '<message>' }.
 */
export const validateScreenJson = (type, content) => {
    try {
        switch (type) {
            case 'playdynamex':
            case 'simulate':
            case 'integrate':
                validatePortable(content, type)
                break
            case 'assembly':
                validateAssembly(content)
                break
            case 'hex':
                validateHex(content)
                break
            case 'play':
                validatePlay(content)
                break
            default:
                throw new Error(`Unsupported screen type "${type}"`)
        }
        return { valid: true }
    } catch (err) {
        return { valid: false, error: err.message }
    }
}

/**
 * Transform a validated uploaded object into the internal format for the target
 * screen, restoring board-specific fields from the target project/board.
 * `ctx` = { projectId, device }.
 */
export const prepareImportJson = (type, content, ctx = {}) => {
    switch (type) {
        case 'assembly':
            return importAssembly(content)
        case 'hex':
            return importHex(content)
        case 'play':
            return importPlay(content, ctx)
        case 'playdynamex':
        case 'simulate':
        case 'integrate':
            return importPortable(content)
        default:
            return importPortable(content)
    }
}

export const isSupportedScreenType = (type) =>
    [...PORTABLE_TYPES, 'assembly', 'hex', 'play'].includes(type)
